// Accept an invitation at /invitations/{token} (UC-IDENTITY-011): the organisation, name, role and Control Id are shown,
// the user sets a password and a Control PIN; a refused PIN keeps the password and asks again; success starts the session.
import { PASSWORD_MIN_LENGTH, PIN_MAX_LENGTH, pinProblem } from "@visionx/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatDateTime } from "../lib/format";
import { loadSession } from "../session/store";
import { Alert, Field, KV, TextInput } from "../ui/bits";
import { Mark } from "../ui/icons";
import { roleLabel } from "./common";

export function InvitationAccept() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["invitation", token], queryFn: () => auth.invitation(token) });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [passwordDone, setPasswordDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  const inv = q.data?.data;
  const needPassword = !(inv?.passwordSet || passwordDone);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPinError(null);
    if (needPassword) {
      if (password.length < PASSWORD_MIN_LENGTH) { setError(`Use at least ${PASSWORD_MIN_LENGTH} characters for the password.`); return; }
      if (password !== confirm) { setError("The two passwords do not match."); return; }
    }
    const problem = pinProblem(pin, {});
    if (problem) { setPinError(problem === "trivial" ? "Too easy to guess: no repeated digits, runs like 1234, or your Control ID." : `Use 4 to ${PIN_MAX_LENGTH} digits.`); return; }
    if (pin !== pinConfirm) { setPinError("The two PINs do not match."); return; }
    setBusy(true);
    try {
      const { data } = await auth.acceptInvitation(token, { ...(needPassword ? { password } : {}), pin });
      if (data.requiresPinSetup) { setPasswordDone(true); setPinError("The PIN was not accepted. Choose another."); return; }
      // The session is open: enter the app (step 6). The session gate shows the shell, or the PIN set-up if it is still owed.
      // Staying here would leave the spent link on screen, and a second press would answer "expired or already used".
      await loadSession({ credentialAccepted: true });
      navigate("/today", { replace: true });
    } catch (err) {
      if (isApiError(err) && err.code === "VALIDATION_FAILED") {
        const p = err.field("pin");
        const pw = err.field("password");
        if (pw) setError(pw.message);
        if (p) { setPinError(p.message); if (needPassword) setPasswordDone(true); }
        if (!p && !pw) setError(describeError(err));
      } else if (isApiError(err) && err.code === "NOT_FOUND") {
        setError("This invitation link has expired or was already used. Ask your administrator to send a new one.");
      } else {
        setError(describeError(err));
      }
      setPin("");
      setPinConfirm("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="screen" id="s-signin" data-bare="true">
      <div className="content">
        <div className="wrap">
          <div className="cards">
            <div className="stack card">
              <form className="panel" onSubmit={(e) => void submit(e)}>
                <div className="panel-body" style={{ padding: 24, gap: 16 }}>
                  <div className="logo"><Mark className="vxmark" /><span>Vision X</span></div>
                  {q.isPending && <div className="help">Checking the invitation…</div>}
                  {q.isError && (
                    <>
                      <h1>Invitation not found</h1>
                      <Alert tone="warn"><b>This link has expired or was already used.</b> Invitations work once, for 72 hours. Ask your administrator to send a new one.</Alert>
                      <div className="centre"><Link className="small" to="/signin">Back to sign in</Link></div>
                    </>
                  )}
                  {inv && (
                    <>
                      <div className="stack gap-4">
                        <h1>Join {inv.organisation.tradingName ?? inv.organisation.legalName}</h1>
                        <div className="help">Set your password and Control PIN to enter Vision X. The link works until {formatDateTime(inv.expiresAt)}.</div>
                      </div>
                      <KV rows={[
                        ["Name", `${inv.user.firstName} ${inv.user.lastName}`],
                        ["Email", inv.user.email],
                        ["Role", roleLabel(inv.user.role)],
                        ["Control ID", <span className="mono" key="c">{inv.user.controlIdDisplay}</span>],
                      ]} />
                      {error && <Alert tone="crit">{error}</Alert>}
                      {needPassword ? (
                        <>
                          <Field label="Password" required htmlFor="inv-password" help={`At least ${PASSWORD_MIN_LENGTH} characters`}>
                            <TextInput id="inv-password" type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)} autoFocus />
                          </Field>
                          <Field label="Repeat the password" required htmlFor="inv-confirm">
                            <TextInput id="inv-confirm" type="password" autoComplete="new-password" required value={confirm} disabled={busy} onChange={(e) => setConfirm(e.target.value)} />
                          </Field>
                        </>
                      ) : (
                        <Alert tone="ok"><b>Password set.</b> Now choose your Control PIN.</Alert>
                      )}
                      <Field label="Control PIN" required htmlFor="inv-pin" error={pinError ?? undefined} help={`4 to ${PIN_MAX_LENGTH} digits. Not all the same digit, not a run like 1234, not your Control ID.`}>
                        <TextInput id="inv-pin" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password" required maxLength={PIN_MAX_LENGTH} value={pin} disabled={busy} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} mono />
                      </Field>
                      <Field label="Repeat the PIN" required htmlFor="inv-pin-confirm">
                        <TextInput id="inv-pin-confirm" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="new-password" required maxLength={PIN_MAX_LENGTH} value={pinConfirm} disabled={busy} onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))} mono />
                      </Field>
                      <button className="btn primary full" type="submit" disabled={busy}>{busy ? "Saving…" : "Accept and sign in"}</button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
